import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { FileUploader } from '@/common/components/FileUploader'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import useDebounce from '@/common/hooks/useDebounce'
import { Mapper } from '@/common/services/mapper'
import { ErrorResponse } from '@/common/types/errorResponse'
import { cn } from '@/common/utils/cn'
import { checkIsPdf } from '@/common/utils/fileValidator'
import { FormTransformers } from '@/common/utils/formTransformers'
import { getFilenameFromUrl } from '@/common/utils/getFilenameFromUrl'
import { ModalSendContractEnvelopeToRemoteSigningService } from '@/features/privateCompanyLeadsFunnel/components/ContractSignatureDetails/ClicksignSignature/ModalSendContractEnvelopeToRemoteSigningService'
import { Witnesses } from '@/features/privateCompanyLeadsFunnel/components/ContractSignatureDetails/ClicksignSignature/Witnesses'
import { useSignableContractDefaultSigners } from '@/features/privateCompanyLeadsFunnel/hooks/useSignableContractDefaultSigners'
import { useUpdateContractEnvelope } from '@/features/privateCompanyLeadsFunnel/hooks/useUpdateContractEnvelope'
import { useUpdateContractEnvelopeSignatureType } from '@/features/privateCompanyLeadsFunnel/hooks/useUpdateContractEnvelopeSignatureType'
import {
  contractEnvelopeSchema,
  ContractEnvelopeValidationSchema,
} from '@/features/privateCompanyLeadsFunnel/schemas/contractEnvelope.schema'
import { ContactData, PrivateCompanyLeadData } from '@/features/privateCompanyLeadsFunnel/types/privateCompanyLead'
import { ContractEnvelopeData } from '@/features/privateCompanyLeadsFunnel/types/privateCompanyLeadContractEnvelope'
import { updateContractEnvelopeDefaultValues } from '@/features/privateCompanyLeadsFunnel/utils/updateContractEnvelopeDefaultValues'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { FileBadgeIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

interface CreateClicksignSignatureProps {
  privateCompanyLead: PrivateCompanyLeadData
  contractEnvelope?: ContractEnvelopeData
  contractEnvelopeIsPending?: boolean
  disableFields?: boolean
}

export const CreateClicksignSignature = ({
  privateCompanyLead,
  contractEnvelope,
  contractEnvelopeIsPending,
  disableFields,
}: CreateClicksignSignatureProps) => {
  const queryClient = useQueryClient()

  const [file, setFile] = useState<Blob>()
  const [fileName, setFileName] = useState('')
  const [searchDefaultSigners, setSearchDefaultSigners] = useDebounce('')
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLead.id] })
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLeadContractEnvelope', privateCompanyLead.id] })
  }

  const onError = (error: ErrorResponse) => {
    reset(updateContractEnvelopeDefaultValues(contractEnvelope))
    FormTransformers.errorsTransformer({ setError, setMessages })(error)
  }

  const { mutate: updateSignatureType, isPending: updateSignatureTypeIsPending } =
    useUpdateContractEnvelopeSignatureType({
      id: privateCompanyLead.id,
      onSuccess,
      onError,
    })

  const { data: defaultSigners } = useSignableContractDefaultSigners({
    filters: {
      search: searchDefaultSigners,
    },
    mapper: Mapper.mapToOptions({ labelFieldName: 'full_name', valueFieldName: 'id' }),
  })

  const { mutate: updateContractEnvelope } = useUpdateContractEnvelope({
    id: privateCompanyLead.id,
    file,
    onSuccess,
    onError,
  })

  const {
    control,
    clearErrors,
    trigger,
    getValues,
    setError,
    reset,
    formState: { errors },
  } = useForm<ContractEnvelopeValidationSchema>({
    resolver: zodResolver(contractEnvelopeSchema),
    defaultValues: updateContractEnvelopeDefaultValues(contractEnvelope),
  })

  const onSubmit: SubmitHandler<ContractEnvelopeValidationSchema> = (data) => {
    clearErrors()
    updateContractEnvelope(data)
  }

  const submit = async () => {
    const isValid = await trigger()

    if (!isValid) {
      return
    }

    onSubmit(getValues())
  }

  useEffect(() => {
    if (contractEnvelopeIsPending) {
      return
    }

    reset(updateContractEnvelopeDefaultValues(contractEnvelope))
  }, [contractEnvelopeIsPending])

  return (
    <Box className="flex flex-col gap-y-2 p-4">
      <div className="flex items-start justify-between">
        <p className="text-lg font-bold">Contrato (Assinatura sistema)</p>

        <Button
          type="button"
          onClick={() => updateSignatureType('manual')}
          className="flex items-center gap-x-2 bg-danger-op hover:bg-danger disabled:hover:bg-danger-op"
          disabled={updateSignatureTypeIsPending}
          isLoading={updateSignatureTypeIsPending}
        >
          <p className="text-sm">Optar por assinatura manual</p>

          <FileBadgeIcon className="size-4" />
        </Button>
      </div>

      <FileUploader
        label=""
        title="Anexe a minuta do contrato (PDF)"
        dataTestid="file_to_sign_file_input"
        titleClassName="text-center font-bold"
        onChange={(e) => {
          const file = (e.target as HTMLInputElement & { files: FileList }).files?.[0]
          if (!file || !checkIsPdf(file)) {
            return
          }
          if (file) {
            setFileName(file.name)
            setFile(file)
            submit()
          }
        }}
        className={cn('border-sm', {
          'bg-yellow-op-2 border-none': !file && !contractEnvelope?.file_to_sign?.url,
        })}
        name="document"
        accept="application/pdf"
        fileName={
          fileName
            ? fileName
            : contractEnvelope?.file_to_sign?.url
            ? getFilenameFromUrl(contractEnvelope.file_to_sign?.url)
            : undefined
        }
        currentFileUrl={
          file
            ? URL.createObjectURL(file)
            : contractEnvelope?.file_to_sign?.url
            ? contractEnvelope.file_to_sign?.url
            : undefined
        }
        disabled={disableFields}
      />

      <SelectController
        control={control}
        name="contract_attributes.valid_envelope_attributes.contractee_attributes.signable_id"
        label="Representante legal (Tcard)"
        placeholder="Selecione um usuário Tcard"
        markAsRequired
        triggerOnChange={submit}
        options={defaultSigners || []}
        onInputChange={setSearchDefaultSigners}
        errorMessage={
          errors?.contract_attributes?.valid_envelope_attributes?.contractee_attributes?.signable_id?.message
        }
        isDisabled={disableFields}
      />

      <SelectController
        control={control}
        name="contract_attributes.valid_envelope_attributes.contractor_attributes.signable_id"
        label="Representante legal"
        placeholder="Selecione um Representante Legal"
        markAsRequired
        triggerOnChange={submit}
        options={
          privateCompanyLead?.contacts?.length
            ? Mapper.mapToOptions<ContactData>({ labelFieldName: 'full_name', valueFieldName: 'id' })(
                privateCompanyLead?.contacts,
              )
            : []
        }
        errorMessage={
          errors?.contract_attributes?.valid_envelope_attributes?.contractor_attributes?.signable_id?.message
        }
        isDisabled={disableFields}
      />

      <SelectController
        control={control}
        name="contract_attributes.valid_envelope_attributes.surety_attributes.signable_id"
        label="Fiador"
        placeholder={contractEnvelope?.contractor ? 'Selecione um Fiador' : 'Selecione o representante legal primeiro'}
        triggerOnChange={submit}
        errorMessage={errors?.contract_attributes?.valid_envelope_attributes?.surety_attributes?.signable_id?.message}
        options={[
          { value: '', label: 'Nenhum' },
          ...(privateCompanyLead?.contacts?.length
            ? Mapper.mapToOptions<ContactData>({ labelFieldName: 'full_name', valueFieldName: 'id' })(
                privateCompanyLead?.contacts?.filter(
                  (contact) => contact.id !== contractEnvelope?.contractor?.signable_id,
                ),
              )
            : []),
        ]}
        isDisabled={!contractEnvelope?.contractor || disableFields}
      />

      <Witnesses privateCompanyLeadId={privateCompanyLead.id} contractEnvelope={contractEnvelope} />

      <ModalSendContractEnvelopeToRemoteSigningService
        privateCompanyLeadId={privateCompanyLead.id}
        contractEnvelope={contractEnvelope}
        setMessages={setMessages}
      />

      <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
    </Box>
  )
}
